import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActualitesService } from '../actualites.service';
import { Actualite } from '../actualites-list/actualites-list.component';
import { PermissionService } from '../../../../core/service/permission/permission.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';

@Component({
  selector: 'app-actualites-detail',
  standalone: false,
  templateUrl: './actualites-detail.component.html',
  styleUrls: ['./actualites-detail.component.scss']
})
export class ActualitesDetailComponent implements OnInit, OnDestroy {
  readonly permService = inject(PermissionService);
  actualite = signal<Actualite | null>(null);
  safeContent = signal<SafeHtml>('');
  isLoading = signal(true);
  errorMessage = signal<string | null>(null);

  private readonly arabicRe = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
  private destroy$ = new Subject<void>();

  constructor(
    private actualitesService: ActualitesService,
    private sanitizer: DomSanitizer,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) this.loadActualite(params['id']);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadActualite(id: number): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.actualitesService.getActualiteById(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const a = response.data || response;
          this.actualite.set(a);
          this.safeContent.set(
            this.sanitizer.bypassSecurityTrustHtml(a.news_content || '')
          );
          this.isLoading.set(false);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors du chargement de l\'actualité');
          this.isLoading.set(false);
        }
      });
  }

  isArabic(text: string): boolean {
    return !!text && this.arabicRe.test(text);
  }

  dir(text: string): 'rtl' | 'ltr' {
    return this.isArabic(text) ? 'rtl' : 'ltr';
  }

  onEdit(): void {
    const id = this.actualite()?.news_id;
    if (id) this.router.navigate(['/federation/actualites', id, 'edit']);
  }

  onBack(): void {
    this.router.navigate(['/federation/actualites']);
  }

  statusLabel(status: string): string {
    const map: Record<string, string> = { ACTI: 'En ligne', INAC: 'Hors ligne' };
    return map[status] || status;
  }

  statusBadgeClass(status: string): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  pendingStatus = signal<{ status: 'ACTI' | 'INAC' } | null>(null);
  pendingDelete = signal(false);
  isSaving = signal(false);

  confirmStatus(status: 'ACTI' | 'INAC'): void {
    this.pendingStatus.set({ status });
  }

  cancelStatus(): void {
    this.pendingStatus.set(null);
  }

  applyStatus(): void {
    const a = this.actualite();
    const pending = this.pendingStatus();
    if (!a || !pending) return;
    this.isSaving.set(true);
    this.pendingStatus.set(null);
    this.actualitesService.setStatus(a.news_id, pending.status)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.actualite.update(prev => prev ? { ...prev, news_status: pending.status } : prev);
          this.isSaving.set(false);
          const label = pending.status === 'ACTI' ? 'En ligne' : 'Hors ligne';
          SwalUtils.success('Statut mis à jour', `L'actualité est maintenant ${label}.`);
        },
        error: () => {
          SwalUtils.error('Erreur', 'Impossible de mettre à jour le statut.');
          this.isSaving.set(false);
        }
      });
  }

  confirmDelete(): void {
    this.pendingDelete.set(true);
  }

  cancelDelete(): void {
    this.pendingDelete.set(false);
  }

  applyDelete(): void {
    const a = this.actualite();
    if (!a) return;
    this.isSaving.set(true);
    this.pendingDelete.set(false);
    this.actualitesService.setStatus(a.news_id, 'ARCH')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.router.navigate(['/federation/actualites']),
        error: () => {
          SwalUtils.error('Erreur', 'Impossible d\'archiver cette actualité.');
          this.isSaving.set(false);
        }
      });
  }

  pendingStatusLabel(status: 'ACTI' | 'INAC'): string {
    const map: Record<string, string> = { ACTI: 'Publier', INAC: 'Dépublier' };
    return map[status] || status;
  }
}
